import React, { useState, useEffect } from "react";
import { Header } from "../../components/modules";
import { MdImage } from "react-icons/md";
import { Spinner } from "reactstrap";
import { TbPhotoPlus } from "react-icons/tb";
import Context from "../../Context";
import { Switch, useToast } from "../../components/ui";

const LANGS = [
  { key: "th", label: "ไทย 🇹🇭" },
  { key: "en", label: "English 🇬🇧" },
];

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const Banners = () => {
  const { configs, callApi, updateConfig } = Context();
  const { toast } = useToast();
  const [loading, setLoading] = useState({});
  const [bannersList, setBannersList] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Sync with global configs and migrate object schema if necessary
  useEffect(() => {
    if (configs?.banners) {
      const raw = configs.banners;
      let list = [];
      if (Array.isArray(raw)) {
        list = raw;
      } else if (typeof raw === "object") {
        list = Object.keys(raw)
          .sort((a, b) => Number(a) - Number(b))
          .map((key) => {
            const slot = raw[key];
            return slot.images
              ? slot
              : {
                images: { th: slot.url || "", en: "" },
                link: slot.link || "",
                enabled: slot.enabled !== undefined ? Boolean(slot.enabled) : false,
              };
          });
      }
      setBannersList(list);
    } else {
      setBannersList([]);
    }
  }, [configs]);

  const showToast = (success) => {
    toast({
      className: success ? "bg-[rgb(59,176,101)]" : "bg-[#c15b78]",
      title: success ? "อัพเดตข้อมูลสำเร็จ" : "อัพเดตข้อมูลไม่สำเร็จ",
    });
  };

  const callEditBanner = async (index, payload) => {
    const key = `${index}-${payload.lang || "meta"}`;
    setLoading((prev) => ({ ...prev, [key]: true }));
    const { status } = await callApi(
      "configs-editBanner",
      { index, ...payload },
      { notShowLoading: true }
    );
    showToast(status);
    setLoading((prev) => ({ ...prev, [key]: false }));
    if (status) updateConfig();
    return status;
  };

  const handleImageUpload = async (index, lang, file) => {
    if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
      alert("อัพโหลดได้เฉพาะไฟล์รูปภาพ นามสกุล jpg หรือ png");
      return;
    }
    const loadKey = `${index}-${lang}`;
    setLoading((prev) => ({ ...prev, [loadKey]: true }));
    try {
      const base64 = await fileToBase64(file);
      await callEditBanner(index, { image: base64, lang });
    } catch (e) {
      alert("เกิดข้อผิดพลาดในการอ่านไฟล์รูปภาพ");
    } finally {
      setLoading((prev) => ({ ...prev, [loadKey]: false }));
      const inputEl = document.getElementById(`banner-file-${index}-${lang}`);
      if (inputEl) inputEl.value = "";
    }
  };

  const handleLinkChange = async (index, link) => {
    const current = bannersList[index]?.link || "";
    if (link === current) return;
    if (!window.confirm("ท่านต้องการแก้ไข link ใช่หรือไม่")) return;
    await callEditBanner(index, { link });
  };

  const handleToggle = async (index, enabled) => {
    if (!window.confirm(`ท่านต้องการ${enabled ? "เปิด" : "ปิด"}แบนเนอร์นี้ใช่หรือไม่`)) return;
    await callEditBanner(index, { enabled });
  };

  // Drag and Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const list = [...bannersList];
    const draggedItem = list[draggedIndex];
    list.splice(draggedIndex, 1);
    list.splice(index, 0, draggedItem);

    setDraggedIndex(index);
    setBannersList(list);
  };

  const handleDragEnd = async () => {
    setDraggedIndex(null);
    setLoading((prev) => ({ ...prev, saveOrder: true }));
    const { status } = await callApi("configs-editBanner", { banners: bannersList });
    showToast(status);
    if (status) updateConfig();
    setLoading((prev) => ({ ...prev, saveOrder: false }));
  };

  // Add and Delete handlers
  const handleAddBanner = async () => {
    const defaultSlot = { images: { th: "", en: "" }, link: "", enabled: false };
    const newList = [...bannersList, defaultSlot];
    setLoading((prev) => ({ ...prev, saveOrder: true }));
    const { status } = await callApi("configs-editBanner", { banners: newList });
    showToast(status);
    if (status) updateConfig();
    setLoading((prev) => ({ ...prev, saveOrder: false }));
  };

  const handleDeleteBanner = async (index) => {
    if (!window.confirm("ท่านต้องการลบแบนเนอร์นี้ใช่หรือไม่?")) return;
    const newList = bannersList.filter((_, idx) => idx !== index);
    setLoading((prev) => ({ ...prev, saveOrder: true }));
    const { status } = await callApi("configs-editBanner", { banners: newList });
    showToast(status);
    if (status) updateConfig();
    setLoading((prev) => ({ ...prev, saveOrder: false }));
  };

  return (
    <div className="h-fit min-w-[600px] w-full pb-10">
      <Header
        header="Settings"
        subHeader="แบนเนอร์"
        icon={<MdImage className="text-blue" />}
      />

      <div className="relative col-span-3 rounded-lg mt-6 overflow-hidden shadow-md bg-white min-w-48 w-full h-fit">
        <div className="p-4 flex justify-between items-center border-b border-[#E5E7E9] bg-gray-50">
          <div>
            <h3 className="text-xl font-bold text-gray-800">จัดการแบนเนอร์</h3>
            <p className="text-sm font-semibold text-gray-500">สามารถลากสลับตำแหน่ง (Drag & Drop) และเพิ่ม/ลบ แบนเนอร์ได้ตามต้องการ</p>
          </div>
          <button
            onClick={handleAddBanner}
            disabled={loading.saveOrder || !configs}
            className="py-2.5 px-4 text-sm font-bold rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors flex items-center gap-1.5"
          >
            <span>+</span> เพิ่มแบนเนอร์
          </button>
        </div>

        {bannersList.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-gray-400">
            <MdImage className="w-16 h-16 mb-2" />
            <p className="text-sm font-semibold text-gray-500">ยังไม่มีข้อมูลแบนเนอร์ในระบบ</p>
            <button
              onClick={handleAddBanner}
              disabled={loading.saveOrder || !configs}
              className="mt-4 py-2.5 px-4 text-sm font-bold rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            >
              เพิ่มแบนเนอร์รูปแรก
            </button>
          </div>
        ) : (
          bannersList.map((slot, index) => {
            const isDragging = draggedIndex === index;

            return (
              <div
                key={index}
                draggable="true"
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`p-4 border-b border-[#E5E7E9] transition-all duration-200 ${isDragging
                  ? "bg-blue-50/50 border-y-2 border-dashed border-blue-400 opacity-60 scale-[0.99]"
                  : "hover:bg-gray-50/60"
                  }`}
              >
                {/* Slot header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {/* Drag Handle Icon */}
                    <div className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-200 transition-colors">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2.5"
                          d="M4 8h16M4 16h16"
                        />
                      </svg>
                    </div>
                    <p className="text-sm font-bold text-gray-700">แบนเนอร์ที่ {index + 1} (1,400x360)</p>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-semibold text-gray-500">เปิดใช้งาน</p>
                      <Switch
                        disabled={loading[`${index}-meta`] || !configs || loading.saveOrder}
                        checked={Boolean(slot.enabled)}
                        onCheckedChange={(val) => handleToggle(index, val)}
                      />
                    </div>
                    <button
                      onClick={() => handleDeleteBanner(index)}
                      disabled={loading.saveOrder}
                      className="text-sm font-semibold text-red-500 hover:text-red-700 transition-colors"
                    >
                      ลบ
                    </button>
                  </div>
                </div>

                {/* Language image upload grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 pl-8">
                  {LANGS.map(({ key: lang, label }) => {
                    const imgUrl = slot.images?.[lang] || "";
                    const loadKey = `${index}-${lang}`;
                    const isLoading = loading[loadKey];

                    return (
                      <div key={lang} className="flex flex-col gap-1">
                        <p className="text-xs font-semibold text-gray-500">{label}</p>
                        <label
                          htmlFor={`banner-file-${index}-${lang}`}
                          className="cursor-pointer w-full"
                        >
                          {isLoading ? (
                            <div className="flex items-center justify-center h-24 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                              <Spinner className="w-5 h-5 text-gray-400" />
                            </div>
                          ) : imgUrl ? (
                            <div className="relative group rounded-lg overflow-hidden shadow-sm border border-gray-200">
                              <img
                                src={imgUrl}
                                alt={`banner ${index + 1} ${lang}`}
                                className="w-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <p className="text-white text-xs font-semibold">คลิกเพื่อเปลี่ยนรูป</p>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center h-24 bg-gray-50 hover:bg-gray-100 rounded-lg border border-dashed border-gray-300 transition-colors">
                              <TbPhotoPlus className="w-7 h-7 text-gray-400" />
                              <p className="text-xs text-gray-400 mt-1">คลิกเพื่ออัพโหลด ({lang.toUpperCase()})</p>
                            </div>
                          )}
                        </label>
                        <input
                          id={`banner-file-${index}-${lang}`}
                          type="file"
                          accept="image/jpeg,image/png,image/jpg"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleImageUpload(index, lang, file);
                          }}
                        />
                      </div>
                    );
                  })}
                </div>

                {/* Link input (shared across languages) */}
                <div className="flex flex-col gap-1 pl-8">
                  <p className="text-sm font-semibold text-gray-600">Link ปลายทาง (ทุกภาษา)</p>
                  <input
                    type="text"
                    className="border border-gray-300 py-1.5 px-2 text-sm rounded-lg placeholder:text-gray-400 disabled:opacity-60 w-full"
                    disabled={loading[`${index}-meta`] || !configs || loading.saveOrder}
                    defaultValue={slot.link || ""}
                    placeholder="https://..."
                    onBlur={(e) => handleLinkChange(index, e.target.value)}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
